import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useRecipe } from '../../contexts/RecipeContext';
import '../../styles/recipes/RecipeReadView.css';

const RecipeReadView = ({ recipe, servings, onClose }) => {
  const { t } = useLanguage();
  const { scaleIngredients } = useRecipe();

  const scaledIngredients = useMemo(() => {
    if (!recipe?.ingredients || recipe.ingredients.length === 0) return [];
    return scaleIngredients(recipe.ingredients, recipe.baseServings || 4, servings);
  }, [recipe, servings, scaleIngredients]);

  if (!recipe) return null;

  return createPortal(
    <div className="recipe-read-view-overlay">
      <div className="recipe-read-view">
        <div className="recipe-read-view-toolbar">
          <button className="recipe-read-view-btn" onClick={() => window.print()}>
            {t('recipes.print') || 'Drucken'}
          </button>
          <button className="recipe-read-view-btn" onClick={onClose}>
            {t('recipes.close') || 'Schließen'}
          </button>
        </div>

        <div className="recipe-read-view-page">
          <h1 className="recipe-read-view-title">{recipe.title}</h1>

          {recipe.description && (
            <p className="recipe-read-view-description">{recipe.description}</p>
          )}

          <div className="recipe-read-view-meta">
            <span>{t('recipes.servings') || 'Portionen'}: {servings}</span>
            {recipe.preparationTime && (
              <span>{t('recipes.form.prepTime') || 'Vorbereitungszeit'}: {recipe.preparationTime} min</span>
            )}
            {recipe.cookingTime && (
              <span>{t('recipes.form.cookTime') || 'Kochzeit'}: {recipe.cookingTime} min</span>
            )}
          </div>

          <hr className="recipe-read-view-divider" />

          <h2 className="recipe-read-view-subheading">{t('recipes.ingredients') || 'Zutaten'}</h2>

          {scaledIngredients.length > 0 ? (
            <ul className="recipe-read-view-ingredients">
              {scaledIngredients.map((ingredient, index) => (
                <li key={index}>
                  <span className="recipe-read-view-qty">
                    {ingredient.quantity} {ingredient.unit}
                  </span>{' '}
                  <span className="recipe-read-view-name">{ingredient.name}</span>
                  {ingredient.note && (
                    <span className="recipe-read-view-note"> ({ingredient.note})</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="recipe-read-view-empty">
              {t('recipes.noIngredients') || 'Keine Zutaten für dieses Rezept hinterlegt.'}
            </p>
          )}

          {recipe.steps && recipe.steps.length > 0 && (
            <>
              <hr className="recipe-read-view-divider" />

              <h2 className="recipe-read-view-subheading">{t('recipes.instructions') || 'Zubereitung'}</h2>

              <ol className="recipe-read-view-steps">
                {recipe.steps.map((step, index) => (
                  <li key={index}>{step}</li>
                ))}
              </ol>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RecipeReadView;
